// src/modules/app-views/collaborators/pages/Init/modals/CollaboratorsParametersModal/tabs/FormSettingsTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo } from 'react';
import { Card, Switch, Flex, Typography, Segmented } from 'antd';
import { ShopOutlined, UserOutlined, SwapOutlined, GlobalOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const FormSettingsTab = ({ loading, setLoading }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [settings, setSettings] = useState(() => {
    const rawData = appService.getModulePreferences({ category: 'form' });
    return {
      // Entity type configuration
      entityPersonType: rawData?.entityPersonType || 'manual',
      entityPersonDefault: rawData?.entityPersonDefault || 'company',
      // Internationalization
      allowForeignCountries: rawData?.allowForeignCountries ?? false,
    };
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. COMPUTED VALUES
  // ─────────────────────────────────────────────────────────────────────────────
  // Check if hybrid mode (manual selection)
  const isHybridMode = settings.entityPersonType === 'manual';

  // Entity person type options - must be inside component for translations to work
  const entityPersonTypeOptions = useMemo(
    () => [
      {
        value: 'manual',
        label: translateX('both_types'),
        icon: <SwapOutlined />,
      },
      {
        value: 'company',
        label: translateX('legal_person'),
        icon: <ShopOutlined />,
      },
      {
        value: 'person',
        label: translateX('individual_person'),
        icon: <UserOutlined />,
      },
    ],
    []
  );

  // Default entity type options (when manual mode)
  const entityDefaultOptions = useMemo(
    () => [
      {
        value: 'company',
        label: translateX('legal_person'),
        icon: <ShopOutlined />,
      },
      {
        value: 'person',
        label: translateX('individual_person'),
        icon: <UserOutlined />,
      },
    ],
    []
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const saveSettings = useCallback(
    async newSettings => {
      setLoading(true);
      try {
        // Get existing data to preserve other fields
        const existingData = appService.getModulePreferences({ category: 'form' });

        const dataToSave = {
          ...existingData,
          entityPersonType: newSettings.entityPersonType,
          entityPersonDefault: newSettings.entityPersonDefault,
          allowForeignCountries: newSettings.allowForeignCountries,
        };

        await appService.updateModulePreferences({
          category: 'form',
          data: dataToSave,
        });

        appService.message({ flag: 's', text: 'saved_successfully' });
      } catch (error) {
        console.error('[FormSettingsTab] Save error:', error);
        appService.message({ flag: 'e', text: 'error_saving' });
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  const handleEntityPersonTypeChange = useCallback(
    async value => {
      let newSettings = { ...settings, entityPersonType: value };

      // Auto-set default based on selected type
      if (value === 'person') {
        newSettings.entityPersonDefault = 'person';
      } else if (value === 'company') {
        newSettings.entityPersonDefault = 'company';
      }
      // For 'manual', keep the current default

      setSettings(newSettings);
      await saveSettings(newSettings);
    },
    [settings, saveSettings]
  );

  const handleEntityDefaultChange = useCallback(
    async value => {
      const newSettings = { ...settings, entityPersonDefault: value };
      setSettings(newSettings);
      await saveSettings(newSettings);
    },
    [settings, saveSettings]
  );

  const handleAllowForeignCountriesChange = useCallback(
    async checked => {
      const newSettings = { ...settings, allowForeignCountries: checked };
      setSettings(newSettings);
      await saveSettings(newSettings);
    },
    [settings, saveSettings]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        height: 'calc(80vh - 240px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        // paddingRight: 8,
        // paddingBottom: 16,
      }}
    >
      <Flex vertical gap={16}>
        {/* Entity Type Configuration */}
        <Card title={translateX('data_types')} size="small">
          <Flex vertical gap={16}>
            <div>
              <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
                {translateX('person_type')}
              </Text>
              <Segmented
                block
                value={settings.entityPersonType}
                onChange={handleEntityPersonTypeChange}
                disabled={loading}
                options={entityPersonTypeOptions.map(opt => ({
                  value: opt.value,
                  label: (
                    <Flex
                      vertical
                      align="center"
                      justify="center"
                      gap={2}
                      style={{ padding: '14px 0 4px' }}
                    >
                      {opt.icon}
                      <span style={{ fontSize: 11 }}>{opt.label}</span>
                    </Flex>
                  ),
                }))}
              />
            </div>

            {isHybridMode && (
              <div>
                <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
                  {translateX('initial_pattern')}
                </Text>
                <Segmented
                  block
                  value={settings.entityPersonDefault}
                  onChange={handleEntityDefaultChange}
                  disabled={loading}
                  options={entityDefaultOptions.map(opt => ({
                    value: opt.value,
                    label: (
                      <Flex
                        vertical
                        align="center"
                        justify="center"
                        gap={2}
                        style={{ padding: '14px 0 4px' }}
                      >
                        {opt.icon}
                        <span style={{ fontSize: 11 }}>{opt.label}</span>
                      </Flex>
                    ),
                  }))}
                />
                <Text type="secondary" style={{ fontSize: 11, marginTop: 8, display: 'block' }}>
                  {translateX('initial_pattern_description')}
                </Text>
              </div>
            )}
          </Flex>
        </Card>

        {/* Internationalization Configuration */}
        <Card title={translateX('internationalization')} size="small">
          <Flex justify="space-between" align="center" style={{ padding: '14px 0 4px' }}>
            <Flex vertical gap={2}>
              <Flex align="center" gap={8}>
                <GlobalOutlined style={{ color: 'var(--ant-color-primary)' }} />
                <Text style={{ fontSize: 13 }}>{translateX('allow_foreign_countries')}</Text>
              </Flex>
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 22 }}>
                {translateX('allow_foreign_countries_description')}
              </Text>
            </Flex>
            <Switch
              checked={settings.allowForeignCountries}
              onChange={handleAllowForeignCountriesChange}
              disabled={loading}
              size="small"
            />
          </Flex>
        </Card>
      </Flex>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default FormSettingsTab;
